import React, { useRef, useState } from 'react';

import Breadcrumb, { BreadcrumbList } from 'components/shared/Breadcrumb/Breadcrumb';
import { useBreadcrumbContext } from 'components/shared/Breadcrumb/context/BreadcrumbContext';
import Button from 'components/shared/Button/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Modal from 'components/shared/Modal/Modal';

import useOpenModal from '../../lib/useOpenModal';
import PayerForm from '../PayerForm/PayerForm';
import { usePayerForm } from '../PayerForm/lib/usePayerForm';

import AddPayerModalHeader from './components/AddPayerModalHeader';
import AddPayerTable from './components/AddPayerTable';

const STEPS = [
  { name: 'Add payers', title: 'Add payers' },
  { name: 'Request new payer', title: 'Request new payer' }
];

const AddPayerStepBody = ({
  practiceId,
  isFormLoading,
  formikRef,
  addPayerTableRef,
  requestNewPayerFormRef,
  handleClose,
  setSelectedRows
}) => {
  const { step } = useBreadcrumbContext();
  const openModal = useOpenModal();

  if (step === 0) {
    return (
      <AddPayerTable
        ref={addPayerTableRef}
        practiceId={practiceId}
        handleClose={handleClose}
        setSelectedRows={setSelectedRows}
      />
    );
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <PayerForm
        ref={requestNewPayerFormRef}
        practiceId={practiceId}
        isLoading={isFormLoading}
        openModal={openModal}
        formik={formikRef.current}
      />
    </div>
  );
};

const AddPayerFooter = ({
  selectedRows,
  isFormLoading,
  addPayerTableRef,
  requestNewPayerFormRef,
  handleCloseWithReset
}) => {
  const { step } = useBreadcrumbContext();

  if (step === 0) {
    const count = selectedRows.length;
    return (
      <div className="flex w-full items-center justify-between gap-2 bg-white">
        <div className="text-sm text-neutral-700">{count > 0 && `${count} items selected`}</div>
        <div className="flex gap-2">
          <Button
            variant="tertiary"
            color="neutral"
            text="Cancel"
            onClick={handleCloseWithReset}
            size="small"
          />
          <Button
            variant="primary"
            text="Add payers"
            onClick={() => addPayerTableRef.current?.handleAddPayers(selectedRows)}
            isDisabled={count === 0}
            size="small"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center justify-end gap-2 bg-white">
      <Button
        variant="tertiary"
        color="neutral"
        text="Cancel"
        onClick={handleCloseWithReset}
        size="small"
      />
      <Button
        variant="primary"
        text="Request new payer"
        onClick={() => requestNewPayerFormRef.current?.submitForm()}
        isLoading={isFormLoading}
        size="small"
      />
    </div>
  );
};

const HeaderAdapter = ({ onClearSelection }) => {
  const { step, nextCrumb } = useBreadcrumbContext();
  if (step !== 0) return null;
  return (
    <AddPayerModalHeader
      requestNewPayerTab={STEPS[1]}
      onRequestNewPayer={() => {
        onClearSelection();
        nextCrumb(STEPS[1]);
      }}
    />
  );
};

const AddPayerModalInner = ({
  practiceId,
  isFormLoading,
  formikRef,
  selectedRows,
  setSelectedRows,
  addPayerTableRef,
  requestNewPayerFormRef,
  handleCloseWithReset,
  isOpen
}) => {
  const { nextCrumb } = useBreadcrumbContext();

  const handleClose = () => {
    nextCrumb(STEPS[0]);
    handleCloseWithReset();
  };

  const customHeader = (
    <div className="flex w-full items-center justify-between gap-4">
      <BreadcrumbList />
      <HeaderAdapter onClearSelection={() => setSelectedRows([])} />
    </div>
  );

  return (
    <Modal
      width="medium"
      headerClassName="px-5 py-3"
      isOpen={isOpen}
      handleClose={handleClose}
      customHeader={customHeader}
      footer={
        <AddPayerFooter
          selectedRows={selectedRows}
          isFormLoading={isFormLoading}
          addPayerTableRef={addPayerTableRef}
          requestNewPayerFormRef={requestNewPayerFormRef}
          handleCloseWithReset={handleClose}
        />
      }>
      <AddPayerStepBody
        practiceId={practiceId}
        isFormLoading={isFormLoading}
        formikRef={formikRef}
        addPayerTableRef={addPayerTableRef}
        requestNewPayerFormRef={requestNewPayerFormRef}
        handleClose={handleClose}
        setSelectedRows={setSelectedRows}
      />
    </Modal>
  );
};

const AddPayerModal = ({ isOpen, practiceId, handleClose }) => {
  const addPayerTableRef = useRef();
  const requestNewPayerFormRef = useRef();
  const formikRef = useRef();

  const [selectedRows, setSelectedRows] = useState([]);

  const { formik, isLoading: isFormLoading } = usePayerForm({
    onSuccess: handleClose,
    practiceId
  });

  formikRef.current = formik;

  const handleCloseWithReset = () => {
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
    setSelectedRows([]);
    handleClose();
  };

  return (
    <Breadcrumb steps={[STEPS[0]]} initialStep={0}>
      <AddPayerModalInner
        practiceId={practiceId}
        isFormLoading={isFormLoading}
        formikRef={formikRef}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        addPayerTableRef={addPayerTableRef}
        requestNewPayerFormRef={requestNewPayerFormRef}
        handleCloseWithReset={handleCloseWithReset}
        isOpen={isOpen}
      />
    </Breadcrumb>
  );
};

export default withErrorBoundary(AddPayerModal);
